import type { ReactNode } from "react";
import { cn } from "../../utils/cn";

interface StatCardProps {
  label: string;
  value: ReactNode;
  trend?: number;
  trendLabel?: string;
  icon?: string;
  iconClass?: string;
  /** Overdue / negative amounts can tint the value red. */
  tone?: "default" | "danger" | "success" | "warning" | "accent";
  onClick?: () => void;
}

const VALUE_TONE: Record<NonNullable<StatCardProps["tone"]>, string> = {
  default: "text-foreground-950",
  danger: "text-danger-600",
  success: "text-success-600",
  warning: "text-warning-600",
  accent: "text-accent-600",
};

export function StatCard({ label, value, trend, trendLabel, icon, iconClass, tone = "default", onClick }: StatCardProps) {
  const positive = (trend ?? 0) >= 0;
  return (
    <div
      onClick={onClick}
      className={cn(
        "rounded-lg border-2 border-background-300 bg-background-50 p-4 shadow-sm transition-all",
        onClick && "cursor-pointer hover:border-primary-400 hover:shadow-md hover:scale-[1.01]"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-xs font-medium text-foreground-500">{label}</p>
          <p className={cn("tnum mt-1.5 text-2xl font-semibold leading-none", VALUE_TONE[tone])}>{value}</p>
        </div>
        {icon ? (
          <div className={cn("flex h-9 w-9 flex-none items-center justify-center rounded-md", iconClass ?? "bg-secondary-100 text-secondary-700")}>
            <i className={`${icon} text-lg`} />
          </div>
        ) : null}
      </div>
      {trend !== undefined ? (
        <div className="mt-3 flex items-center gap-1.5 text-xs">
          <span className={cn("tnum inline-flex items-center gap-0.5 font-medium", positive ? "text-success-600" : "text-danger-600")}>
            <i className={positive ? "ri-arrow-up-line" : "ri-arrow-down-line"} />
            {Math.abs(trend)}%
          </span>
          <span className="text-foreground-500">{trendLabel ?? "vs last month"}</span>
        </div>
      ) : null}
    </div>
  );
}